import { useId, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SettingsNavItem {
  id: string;
  label: string;
  href: string;
}

export interface SettingsNavGroup {
  /** e.g. "Organization" / "Modules" / "Administration" (PAGE_TEMPLATES.md § T5). */
  label: string;
  items: SettingsNavItem[];
}

export interface SettingsNavProps {
  nav: SettingsNavGroup[];
  activeId?: string;
}

/**
 * The T5 settings sub-nav (PAGE_TEMPLATES.md § T5, frame 8d): a 212px grouped column of links,
 * collapsing to a labelled `<Select>` below 1024px, where the column has no room beside the content.
 */
export function SettingsNav({ nav, activeId }: SettingsNavProps) {
  const navigate = useNavigate();
  const isCompact = useIsMobile(1024);
  const compactNavId = useId();
  const flatItems = nav.flatMap((group) => group.items);
  const activeItem = flatItems.find((item) => item.id === activeId);

  if (isCompact) {
    return (
      <div className="w-full">
        {/* Visible label (ACCESSIBILITY.md § T5: "every control has a visible
            <label>") -- an aria-label alone leaves sighted users guessing what
            the current value (e.g. "Giving") is a selector FOR. */}
        <label
          htmlFor={compactNavId}
          className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Settings section
        </label>
        <Select
          value={activeId}
          onValueChange={(id) => {
            const item = flatItems.find((candidate) => candidate.id === id);
            if (item) navigate(item.href);
          }}
        >
          <SelectTrigger id={compactNavId} className="w-full">
            <SelectValue>{activeItem?.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {nav.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.items.map((item) => (
                  <SelectItem key={item.id} value={item.id} className="min-h-11">
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <nav aria-label="Settings" className="w-[212px] shrink-0 space-y-6">
      {nav.map((group) => (
        <div key={group.label}>
          <h2 className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label}
          </h2>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "block min-h-11 rounded-sm px-2 py-1.5 text-sm leading-tight lg:min-h-6",
                      isActive
                        ? "bg-accent font-medium text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export interface SettingsNavFrameProps extends SettingsNavProps {
  children: ReactNode;
}

/**
 * The T5 two-column frame: the settings sub-nav, then the content column. `SettingsPageTemplate`
 * draws its page inside it, and a settings host draws it around a page that does not render the
 * template yet, so both kinds of page put the nav in the same place. An empty `nav` draws the
 * content column alone.
 */
export function SettingsNavFrame({ nav, activeId, children }: SettingsNavFrameProps) {
  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      {nav.length > 0 ? <SettingsNav nav={nav} activeId={activeId} /> : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
